import type { QueryClient } from "@tanstack/react-query";

import { fetchAuthSession } from "aws-amplify/auth";
import axios, { type AxiosInstance, type AxiosResponse } from "axios";

import type { AxiosInstances } from "../../features/axios-instances";
import {
  getOpaqueAuthorization,
  invalidateSession,
  NoSessionFoundError,
} from "../../public/auth/auth";
import { markAuthRefresh } from "../../public/auth/auth-telemetry";

export function createCatch401ErrorInterceptor(
  // Only `authorize` is needed; narrowing it keeps the interceptor testable
  // without standing up a whole AxiosInstances.
  axiosInstances: Pick<AxiosInstances, "authorize">,
  queryClient: QueryClient,
): (this: AxiosInstance, error: unknown) => Promise<AxiosResponse> {
  // Must stay a `function` expression: the interceptor is invoked with
  // `onRejected.call(instance, error)` and the replay below goes through
  // `this.request(...)`. An arrow would capture the module scope instead and
  // silently skip the replay.
  return async function catch401(
    this: AxiosInstance,
    error: unknown,
  ): Promise<AxiosResponse> {
    if (!axios.isAxiosError(error) || error.response?.status !== 401) {
      throw error;
    }
    markAuthRefresh("catch-401");
    // On an opaque flow (`jwt-as:`, `pat:`) a Cognito refresh cannot help:
    // the credential the platform validates is the opaque token, and the
    // only thing `fetchAuthSession` can hand back is the mint-once Amplify
    // bridge JWT. Authorizing with that would put it on every axios
    // instance — exactly what rule 1 of `public/auth/token-provider.ts`
    // forbids — silently turning a `pat:` session into a JWT one and, on
    // `jwt-as:`, replacing the admin's opaque token with the impersonatee's
    // JWT, which is what records who is behind the impersonation.
    //
    // It could not recover either: the bridge JWT is never re-minted, so
    // `newSession.tokens` is never empty and the invalidation path below
    // would never run — the page would sit in a permanent 401-retry loop.
    // Go straight to invalidation and let fetchSession redirect to sign-in.
    if (getOpaqueAuthorization()) {
      invalidateSession(queryClient);
      throw new NoSessionFoundError();
    }
    // A 401 with no config cannot be replayed at all; surface it as-is
    // rather than burning a refresh on a request we could not re-issue.
    if (!error.config) {
      throw error;
    }
    if (error.config.doNotRetry) {
      throw error;
    }
    if (error.config.doNotAuthRetry) {
      // This request already carried a freshly refreshed token and still got
      // a 401, so the credential is not what the platform is refusing — the
      // session behind it is missing (typically `login-auth` failed during
      // bootstrap and was never retried). Refreshing again cannot fix that;
      // only re-running fetchSession can, since that is what re-establishes
      // the platform session.
      //
      // Rate-limited because a page fans out many queries at once: without
      // the cooldown every one of them would invalidate the session query on
      // the same tick.
      const now = Date.now();
      if (now - lastSessionRepairAt < SESSION_REPAIR_COOLDOWN_MS) {
        throw error;
      }
      lastSessionRepairAt = now;
      invalidateSession(queryClient);
      throw new NoSessionFoundError();
    }
    const newSession = await refreshSession().catch((refreshError: unknown) => {
      if (
        refreshError instanceof Error &&
        (refreshError.name === "NotAuthorizedException" ||
          refreshError.name === "UserUnAuthenticatedException")
      ) {
        // The refresh token itself is dead — fall through to the
        // session-invalidation path below.
        return null;
      }
      // Transient failure (network, timeout…): keep the session and
      // surface the original 401 instead of logging the user out.
      throw error;
    });
    if (!newSession || !newSession.tokens?.accessToken) {
      // The refresh token itself is expired (typically a stale tab
      // refocused after a long idle). Re-run the session query so
      // fetchSession's redirect-to-sign-in logic takes over instead of
      // leaving every query stuck on a NoSessionFoundError crash.
      invalidateSession(queryClient);
      throw new NoSessionFoundError();
    }
    const newAccessToken = newSession.tokens.accessToken
      .toString()
      .replace(/^jwt:/, "");
    const authorization = `Bearer jwt:${newAccessToken}`;
    axiosInstances.authorize(authorization);

    const retryConfig = error.config.originalConfig ?? error.config;
    return await this.request({
      ...retryConfig,
      headers: {
        ...retryConfig.headers,
        Authorization: authorization,
      },
      // We remove cancel token and signal to be able to retry the request,
      // even if the original request was aborted.
      cancelToken: undefined,
      signal: undefined,
      // Only the 401-refresh retry must not loop; a blanket doNotRetry
      // here also disabled the transient network retry, leaving replayed
      // requests with zero protection against connection blips.
      doNotAuthRetry: true,
    });
  };
}

// Session repair re-runs the whole bootstrap (forced Cognito refresh plus the
// platform login-auth call), so it must not fire once per failing query.
const SESSION_REPAIR_COOLDOWN_MS = 30_000;
let lastSessionRepairAt = 0;

export function _resetSessionRepairCooldown(): void {
  lastSessionRepairAt = 0;
}

let refreshSessionPromise: ReturnType<typeof fetchAuthSession> | null = null;

function refreshSession(): ReturnType<typeof fetchAuthSession> {
  if (!refreshSessionPromise) {
    refreshSessionPromise = (async () => {
      try {
        return await fetchAuthSession({ forceRefresh: true });
      } finally {
        refreshSessionPromise = null;
      }
    })();
  }
  return refreshSessionPromise;
}
